import createPersistedReducer from '../../lib/createPersistedReducer.js';
import currentServiceProviderReducerLeadsSlice from './currentServiceProviderReducer/currentServiceProviderReducerLeadsSlice.js';
import currentServiceProviderReducerPipelinesSlice from './currentServiceProviderReducer/currentServiceProviderReducerPipelinesSlice.js';
import mergeByProp from '../../lib/mergeByProp.js';
import setDeep from '../../lib/setDeep.js';
import sortByProp from '../../lib/sortByProp.js';
import mapToCamelCase from '../../lib/mapToCamelCase.js';
import formatUsers from '../lib/formatUsers.js';
import formatLead from '../lib/formatLead.js';
import formatPipelines from '../lib/formatPipelines';

export const initialCurrentServiceProviderState = {
  id: null,
  name: null,
  additionalFields: [],
  creationDatetime: null,
  defaultPipelineId: null,
  templates: [],
  emails: [],
  locations: [],
  servicePersons: [],
  maxProvisionedPhoneNumbers: null,
  trackedPhoneNumbers: [],
  pipelines: [],
  platformRegistrations: [],
  services: [],
  permissions: [],
  users: [],
  notifications: [],
  roles: [],
  destinationPhoneNumbers: undefined,
  selectedPipelineId: null,
  leads: [],
  pipelineLeads: [],
  todos: [],
  utmSources: [],
  utmCampaigns: [],
  officeHours: [],
  stats: {
    unreadCountByStatusId: {},
    readCountByStatusId: {},
  },
};

const legacyKeys = ['leads_total', 'provider_name', 'services_offered', 'reviewloop_accounts', 'email_templates', 'additional_fields', 'event_statuses', 'practitioner_name', 'category_id', 'entity_type_id', 'default_tracked_phone_id', 'default_pipeline_id', 'max_provisioned_phone_numbers', 'creation_datetime', 'tracked_phone_numbers', 'phone_numbers', 'text_templates', 'platform_registrations', 'settings', 'practitionerName', 'phoneNumbers', 'providerName', 'servicesOffered', 'leadModalSelectedPipelineLeadId'];
const persistConfig = {
  key: 'serviceProvidersReducer',
  blacklist: ['leadModalActiveLeadId', 'pipelineLeads', 'leadEntities', 'pipelineEntities', 'stageEntities', 'selectedPipelineId', 'stats', 'todos', 'crmAdwords', 'crmFacebookAds', ...legacyKeys],
};

let currentServiceProvider = createPersistedReducer(
  initialCurrentServiceProviderState,
  {
    ...currentServiceProviderReducerLeadsSlice,
    ...currentServiceProviderReducerPipelinesSlice,

    GET_SERVICE_PROVIDER_SUCCESS: (state, payload) => {
      const serviceProvider = payload.data;
      serviceProvider.emails = (serviceProvider.emails || []).sort(sortByProp('name'));
      serviceProvider.users = formatUsers(serviceProvider.users);
      serviceProvider.locations = (serviceProvider.locations || []).sort(sortByProp('service_location_name'));
      serviceProvider.servicePersons = (serviceProvider.service_persons || []).sort(sortByProp('first_name')).map(mapToCamelCase);
      serviceProvider.pipelines = formatPipelines(serviceProvider.pipelines);
      serviceProvider.destinationPhoneNumbers = (serviceProvider.destination_phone_numbers || []).map(mapToCamelCase);
      serviceProvider.trackedPhoneNumbers = (serviceProvider.tracked_phone_numbers || []).map(mapToCamelCase);
      try {
        serviceProvider.additional_fields = JSON.parse(serviceProvider.additional_fields);
        serviceProvider.destinationPhoneNumbers = (serviceProvider.destinationPhoneNumbers || []).map((n) => ({
          ...n,
          additionalProperties: mapToCamelCase(JSON.parse(n.additionalProperties)),
        }));
      } catch (e) {}
      return {
        ...state,
        ...payload,
        id: serviceProvider.id,
        name: serviceProvider.provider_name,
        additionalFields: serviceProvider.additional_fields,
        creationDatetime: serviceProvider.creation_datetime,
        templates: serviceProvider.templates,
        emails: serviceProvider.emails,
        locations: serviceProvider.locations,
        servicePersons: serviceProvider.servicePersons,
        notifications: (serviceProvider.notifications || []).map(mapToCamelCase)[0],
        defaultTrackedPhoneId: serviceProvider.default_tracked_phone_id,
        destinationPhoneNumbers: serviceProvider.destinationPhoneNumbers,
        maxProvisionedPhoneNumbers: serviceProvider.max_provisioned_phone_numbers,
        trackedPhoneNumbers: serviceProvider.trackedPhoneNumbers,
        pipelines: serviceProvider.pipelines,
        permissions: serviceProvider.permissions,
        platformRegistrations: serviceProvider.platform_registrations,
        services: serviceProvider.services_offered,
        users: serviceProvider.users,
        utmSources: serviceProvider.utm_sources,
        utmCampaigns: serviceProvider.utm_campaigns,
      };
    },

    GET_CUSTOM_UTM_SOURCES_SUCCESS: (state, payload) => ({
      ...state,
      utmSources: [...payload.data.service_provider],
    }),

    GET_CUSTOM_UTM_CAMPAIGNS_SUCCESS: (state, payload) => ({
      ...state,
      utmCampaigns: [...payload.data.service_provider],
    }),

    POST_LEAD_SUCCESS: (state, payload) => {
      return {
        ...state,
        pipelineLeads: mergeByProp('id', state.pipelineLeads, payload.data.pipeline_leads),
      };
    },

    GET_PIPELINE_LEAD_SUCCESS: (state, payload) => {
      const pipelineLead = {
        ...payload.data,
        service_interests: (payload.data.service_interests || []).map((n) => ({
          ...n,
          service_provider_id: payload.data.service_provider_id,
        })),
      };
      return {
        ...state,
        pipelineLeads: mergeByProp('id', state.pipelineLeads, [pipelineLead]),
      };
    },

    PUT_LOCATION_SUCCESS: (state, payload) => {
      const locations = state.locations.map((item) => {
        if (item.id === payload.data.id) {
          item = payload.data;
        }
        return item;
      });

      return {
        ...state,
        locations,
      };
    },

    POST_SERVICE_PROVIDER_SERVICE_SUCCESS: (state, payload) => {
      const copyServicesOffered = state.services.slice(0);
      copyServicesOffered.push(payload.data);
      return {
        ...state,
        services: copyServicesOffered,
      };
    },

    PUT_SERVICE_PROVIDER_SERVICE_SUCCESS: (state, payload) => {
      const copy = state.services.slice();
      let index = null;
      state.services.forEach((item, i) => {
        if (item.id === payload.data.id) {
          index = i;
        }
      });
      copy[index] = payload.data;
      return {
        ...state,
        services: copy,
      };
    },

    CLEAR_PROVIDER_STATE: (state, payload) => initialCurrentServiceProviderState,

    PUT_NOTE_SUCCESS: (state, payload) => {
      return {
        ...state,
        leads: state.leads.map((n) =>
          n.id !== payload.data.lead_id
            ? n
            : {
                ...n,
                notes: mergeByProp('id', n.notes, [payload.data]),
              }
        ),
      };
    },

    POST_CONTACT_OR_CONTACT_DETAIL_SUCCESS: (state, payload) => {
      return {
        ...state,
        leads: mergeByProp('id', state.leads, [formatLead(payload.data)]),
      };
    },

    PUT_LEAD_BUSINESS_NAME_SUCCESS: (state, payload) => {
      const pipelineLeadIds = payload.data.pipeline_leads.map((n) => n.id);
      return {
        ...state,
        leads: state.leads.map((n) => (n.id !== payload.data.id ? n : payload.data)),
        pipelineLeads: state.pipelineLeads.map((n) =>
          !pipelineLeadIds.includes(n.id)
            ? n
            : {
                ...n,
                lead: {
                  ...n.lead,
                  business_name: payload.data.business_name,
                },
              }
        ),
      };
    },
    POST_USER_SUCCESS: (state, payload) => {
      return {
        ...state,
        users: [...state.users, formatUsers([{ ...payload.data }])[0]],
      };
    },
    PUT_USER_SUCCESS: (state, payload) => {
      const currentUserData = (state.users || []).find((n) => n.id === payload.data.id);
      return {
        ...state,
        users: mergeByProp('id', state.users, formatUsers([{ ...(currentUserData ? currentUserData : {}), ...payload.data }])),
      };
    },
    POST_TWILIO_PHONE_NUMBER_PROVISION_SUCCESS: (state, payload) => {
      return {
        ...state,
      };
    },

    GET_MESSAGE_STATS_SUCCESS: (state, payload) => ({
      ...state,
      stats: {
        ...state.stats,
        unreadCountByStatusId: payload.data.unread_count_by_status_id,
        readCountByStatusId: payload.data.read_count_by_status_id,
      },
    }),

    GET_UPDATED_LEAD_CREATION: (state, payload) => {
      console.log('🚀 ~ file: currentServiceProviderReducer.js:473 ~ payload', payload);
      const updatedCount = state.stats.unreadCountByStatusId[1] - 1;
      const data = payload.event.event_data && !payload.event.message ? payload.event.event_data : payload.event.message;
      console.log('🚀 ~ file: currentServiceProviderReducer.js:241 ~ data', data);
      const pipelineLeads = data.lead.pipeline_leads;
      console.log('🚀 ~ file: currentServiceProviderReducer.js:241 ~ pipelineLeads', pipelineLeads);
      console.log('returning', mergeByProp('id', state.pipelineLeads, pipelineLeads).sort(sortByProp('id')));
      return {
        ...state,
        ...((pipelineLeads || []).length ? { pipelineLeads: mergeByProp('id', state.pipelineLeads, pipelineLeads).sort(sortByProp('id')) } : {}),
        stats: {
          ...state.stats,
          unreadCountByStatusId: { ...state.stats.unreadCountByStatusId, [1]: updatedCount < 0 ? 0 : updatedCount },
        },
        leads: mergeByProp('id', state.leads, [formatLead(data.lead)]),
      };
    },

    PATCH_MESSAGE_AS_UNREAD: (state, payload) => ({
      ...state,
      leads: state.leads.map((n) => (n.id !== payload.leadId ? n : setDeep(n, 'stats.unread_incoming_messages', (m) => parseFloat(m || 0) + 1))),
    }),

    PATCH_MESSAGE_AS_READ: (state, payload) => ({
      ...state,
      leads: state.leads.map((n) => (n.id !== payload.leadId ? n : setDeep(n, 'stats.unread_incoming_messages', (m) => (parseFloat(m || 0) < 1 ? 0 : parseFloat(m || 0) - 1)))),
    }),

    PUT_PLATFORM_REGISTRATION_SUCCESS: (state, payload) => ({
      ...state,
      platformRegistrations: payload.data.platform_registrations || state.platformRegistrations,
    }),

    POST_PLATFORM_REGISTRATION_SUCCESS: (state, payload) => ({
      ...state,
      platformRegistrations: payload.data.platform_registrations || state.platformRegistrations,
    }),

    DELETE_PLATFORM_REGISTRATION_SUCCESS: (state, payload) => ({
      ...state,
      platformRegistrations: payload.data.platform_registrations || state.platformRegistrations,
    }),

    GET_TODOS_SUCCESS: (state, payload) => ({
      ...state,
      todos: mergeByProp('id', state.todos, payload.data || []),
    }),
    GET_OVERDUE_TODOS_SUCCESS: (state, payload) => ({
      ...state,
      todos: mergeByProp('id', state.todos, payload.data || []),
    }),
    GET_OVERDUE_TODOS_NOTIFICATIONS_SLIDER_SUCCESS: (state, payload) => ({
      ...state,
      todos: payload.data,
    }),
    GET_TODOS_DUE_TODAY_SUCCESS: (state, payload) => ({
      ...state,
      todos: mergeByProp('id', state.todos, payload.data || []),
    }),
    GET_TODOS_DUE_TODAY_NOTIFICATIONS_SLIDER_SUCCESS: (state, payload) => ({
      ...state,
      todos: payload.data,
    }),
    PATCH_DESTINATION_PHONE: (state, payload) => {
      let userData = state.users.find((n) => n.id === payload.userId);
      let modifiedUserData = { ...userData };
      if (userData) {
        modifiedUserData.userNotificationsDestinationPhoneId = payload.destinationPhoneId;
      }
      const mappedToUsers = state.users.map((n) => (n.id === userData.id ? { ...modifiedUserData } : { ...n }));
      return {
        ...state,
        users: mappedToUsers,
      };
    },
    PATCH_USER_EMAIL_TEXT_NOTIFICATION_TOGGLE: (state, payload) => {
      let userData = state.users.find((n) => n.id === payload.userId);
      let modifiedUserData = { ...userData };
      if (userData) {
        payload.type === 'email' ? (modifiedUserData.userNotificationsEmail = payload.email) : (modifiedUserData.userNotificationsText = payload.text);
      }
      const mappedToUsers = state.users.map((n) => (n.id === userData.id ? { ...modifiedUserData } : { ...n }));
      return {
        ...state,
        users: mappedToUsers,
      };
    },

    PATCH_TODO_SUCCESS: (state, payload) => ({
      ...state,
      leads: state.leads.map((n) =>
        n.id !== payload.data.lead_id
          ? n
          : {
              ...n,
              todos: mergeByProp('id', n.todos || [], [{ ...payload.data }]),
            }
      ),
      todos: mergeByProp('id', state.todos, [{ ...payload.data }]),
    }),

    GET_UPDATED_TODO_EVENT: (state, payload) => {
      return {
        ...state,
        leads: state.leads.map((n) =>
          n.id !== (payload.event.todo || {}).lead_id
            ? n
            : {
                ...n,
                todos: mergeByProp('id', n.todos || [], [payload.event.todo.event_data ? { ...(payload.event.todo.event_data.todo || {}) } : { ...payload.event.todo }]),
              }
        ),
        todos: mergeByProp('id', state.todos, [payload.event.todo.event_data ? { ...(payload.event.todo.event_data.todo || {}) } : { ...payload.event.todo }]),
      };
    },

    REFRESH_TODOS: (state, payload) => ({
      ...state,
      todos: [...(state.todos || [])],
    }),

    POST_TEMPLATE_SUCCESS: (state, payload) => {
      const data = payload.data;
      return {
        ...state,
        templates: [...state.templates, data],
      };
    },

    POST_DESTINATION_PHONE_SUCCESS: (state, payload) => {
      return {
        ...state,
        destinationPhoneNumbers: [
          ...state.destinationPhoneNumbers,
          {
            ...mapToCamelCase({ ...payload.data }),
            additionalProperties: mapToCamelCase((payload.data || {}).additional_properties),
          },
        ],
      };
    },

    PUT_DESTINATION_PHONE_SUCCESS: (state, payload) => {
      return {
        ...state,
        destinationPhoneNumbers: mergeByProp('id', state.destinationPhoneNumbers, [
          {
            ...mapToCamelCase({ ...payload.data }),
            additionalProperties: mapToCamelCase((payload.data || {}).additional_properties),
          },
        ]),
      };
    },

    POST_TRACKED_PHONE_SUCCESS: (state, payload) => ({
      ...state,
      trackedPhoneNumbers: Array.isArray(payload.data) ? payload.data.map(mapToCamelCase) : mergeByProp('id', state.trackedPhoneNumbers, [{ ...mapToCamelCase({ ...payload.data }) }]),
    }),

    PATCH_TEMPLATE_SUCCESS: (state, payload) => ({
      ...state,
      templates: (state.templates || []).map((n) => (n.id === payload.data.id ? { ...n, ...payload.data } : n)),
    }),

    GET_ROLES_SUCCESS: (state, payload) => ({
      ...state,
      roles: (payload.data || []).map(mapToCamelCase),
    }),

    POST_ROLE_SUCCESS: (state, payload) => ({
      ...state,
      roles: (payload.data || []).map(mapToCamelCase),
    }),

    PUT_ROLE_SUCCESS: (state, payload) => ({
      ...state,
      roles: (payload.data || []).map(mapToCamelCase),
    }),

    POST_USER_ROLE_SUCCESS: (state, payload) => ({
      ...state,
      users: formatUsers([...payload.data]),
    }),

    PUT_USER_NOTIFICATION_SUCCESS: (state, payload) => ({
      ...state,
      notifications: (payload.data || []).map(mapToCamelCase)[0],
    }),

    GET_SERVICE_PROVIDER_EMAILS_SUCCESS: (state, payload) => ({
      ...state,
      emails: (payload.data || []).sort(sortByProp('name')),
    }),

    POST_LOCATION_SUCCESS: (state, payload) => ({
      ...state,
      locations: mergeByProp('id', state.locations, [payload.data]),
    }),
    GET_NEXTECH_LEAD_PAYMENTS_SUCCESS: (state, payload) => {
      return {
        ...state,
        leads: state.leads.map((n) =>
          n.id != payload.data.lead_id
            ? n
            : {
                ...n,
                nextechPayments: [...payload.data.payments],
              }
        ),
      };
    },
    GET_OFFICE_HOURS_SUCCESS: (state, payload) => ({
      ...state,
      officeHours: payload.data,
    }),
    SET_OFFICE_HOURS_SUCCESS: (state, payload) => ({
      ...state,
      officeHours: payload.data,
    }),
    UPDATE_NEXTECH_PAYMENT_STATUS_SUCCESS: (state, payload) => {
      const updatePaymentStatus = (payments) => {
        return payments.map((n) => (n.id !== payload.id ? { ...n } : { ...n, status_id: n.status_id === 'active' ? 'inactive' : 'active' }));
      };

      return {
        ...state,
        leads: state.leads.map((n) =>
          n.id != payload.lead_id
            ? n
            : {
                ...n,
                nextechPayments: updatePaymentStatus(n.nextechPayments),
              }
        ),
      };
    },
  },

  persistConfig
);

export default currentServiceProvider;
